import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import APIError from 'types/api_error';
import Application from 'types/application';
import { OrganizationUsageLimits } from 'types/organization_usage_limits';

import { useAPI } from 'hooks/api/useAPI';

export type UpdateWorkspaceUsageLimitsVariables = {
  applicationId: Application['id'];
  limits: Partial<OrganizationUsageLimits>;
};

/**
 * Update usage limits of any workspace of the current organization by id,
 * rather than the currently selected workspace.
 */
export function useUpdateWorkspaceUsageLimits({
  onSettled,
  ...options
}: UseMutationOptions<
  OrganizationUsageLimits,
  APIError,
  UpdateWorkspaceUsageLimitsVariables
> = {}) {
  const queryClient = useQueryClient();
  const { client } = useAPI();

  return useMutation(
    async ({ applicationId, limits }) => {
      const { data } = await client.put<OrganizationUsageLimits>(
        `/internal/apps/${applicationId}/usage_limits`,
        { ...limits }
      );

      return data;
    },
    {
      ...options,
      onSettled(data, error, variables, context) {
        onSettled?.(data, error, variables, context);
        queryClient.invalidateQueries([
          'application-usage-limits',
          variables.applicationId,
        ]);
      },
    }
  );
}
